/**
 * Beat Shop shared visual system — ported verbatim from the Claude Design
 * prototype (feat/beat-shop-page) so every screen keeps the approved look.
 *
 * Themed via CSS variables: light (NOVA brand — #F1F0EC canvas, white cards,
 * #141414 ink, per the main app's MainLayout/Navbar palette) is the default;
 * `.bs-dark` restores the original prototype dark look. PageShell owns the
 * toggle (top-right) and persists the choice in localStorage.
 */
import React from 'react';
import { createPortal } from 'react-dom';
import { MiniPlayerBar } from './MiniPlayerBar';

export const BLUE = '#2F6BFF';

export type BsTheme = 'light' | 'dark';
const THEME_KEY = 'beatshop-theme';

export const getStoredTheme = (): BsTheme =>
  localStorage.getItem(THEME_KEY) === 'dark' ? 'dark' : 'light';

export const BsThemeContext = React.createContext<BsTheme>('light');

/**
 * Renders overlay content (modals) into document.body so the app shell's
 * navbar/scroll wrappers can neither clip nor stack above it, re-applying the
 * beatshop theme class since the portal target sits outside PageShell.
 */
export const BsPortal: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const theme = React.useContext(BsThemeContext);
  return createPortal(
    <div className={theme === 'dark' ? 'beatshop bs-dark' : 'beatshop'}
      style={s("font-family:'Inter',system-ui,sans-serif;-webkit-font-smoothing:antialiased;color:var(--bs-text)")}>
      {children}
    </div>,
    document.body,
  );
};

/** Drag-and-drop handlers for Beat Shop file slots (click-to-choose stays on the hidden input). */
export function fileDropProps(onFiles: (files: FileList) => void) {
  return {
    onDragOver: (e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); },
    onDragEnter: (e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); },
    onDrop: (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.dataTransfer.files?.length) onFiles(e.dataTransfer.files);
    },
  };
}

/** style-string → React style object (keeps the design's inline CSS verbatim) */
export const s = (str: string): React.CSSProperties => {
  const out: Record<string, string> = {};
  for (const decl of str.split(';')) {
    const i = decl.indexOf(':');
    if (i < 0) continue;
    const key = decl.slice(0, i).trim();
    const val = decl.slice(i + 1).trim();
    if (!key) continue;
    const camel = key.replace(/-([a-z])/g, (_m, c: string) => c.toUpperCase());
    out[camel] = val;
  }
  return out as React.CSSProperties;
};

/** seeded PRNG bar generator (prototype's genBars); seed derives from beat id */
export function genBars(seed: number, count: number, min: number, max: number): number[] {
  let a = seed >>> 0;
  const out: number[] = [];
  for (let i = 0; i < count; i++) {
    a = (a * 1664525 + 1013904223) >>> 0;
    out.push(Math.round(min + (max - min) * (a / 4294967296)));
  }
  return out;
}

export function seedFromId(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return h;
}

export const fmtCount = (n: number): string =>
  n >= 1000 ? (n / 1000).toFixed(1).replace(/\.0$/, '') + 'k' : String(n);

export const GlobalStyles: React.FC = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
    .beatshop{
      --bs-bg:#F1F0EC;
      --bs-text:#141414;
      --bs-text-hi:#26262B;
      --bs-text-body:#45454B;
      --bs-text-mid:#5A5A61;
      --bs-text-muted:#63636A;
      --bs-text-label:#71717A;
      --bs-text-dim:#7E7E86;
      --bs-text-faint:#8B8B93;
      --bs-surface:#FFFFFF;
      --bs-card-a:#FFFFFF;
      --bs-card-b:#FBFAF7;
      --bs-card-hover:#F7F6F1;
      --bs-menu:#FFFFFF;
      --bs-thumb:#E9E8E2;
      --bs-input:#FFFFFF;
      --bs-well:#F8F7F2;
      --bs-disabled:#E3E2DC;
      --bs-chip:#F6F5F0;
      --bs-float-chip:rgba(255,255,255,0.78);
      --bs-skel-a:#E6E5DF;
      --bs-skel-b:#DDDCD5;
      --bs-line:rgba(0,0,0,0.07);
      --bs-border:rgba(0,0,0,0.1);
      --bs-border-2:rgba(0,0,0,0.14);
      --bs-border-3:rgba(0,0,0,0.24);
      --bs-row-hover:rgba(0,0,0,0.025);
      --bs-hover:rgba(0,0,0,0.05);
      --bs-wave-off:rgba(0,0,0,0.14);
      --bs-seg-on-bg:#141414;
      --bs-seg-on-text:#FFFFFF;
      --bs-btn-bg:#141414;
      --bs-btn-text:#FFFFFF;
      --bs-blue-soft:#2058D8;
      --bs-green:#189A57;
      --bs-red:#D4372B;
      --bs-amber:#B87900;
      --bs-red-bg:rgba(212,55,43,0.08);
      --bs-red-border:rgba(212,55,43,0.35);
      --bs-amber-bg:rgba(184,121,0,0.12);
      --bs-overlay:rgba(24,24,26,0.45);
      --bs-shadow:rgba(20,20,20,0.18);
      --bs-shadow-strong:rgba(20,20,20,0.22);
    }
    .beatshop.bs-dark{
      --bs-bg:#0A0A0A;
      --bs-text:#fff;
      --bs-text-hi:#EDEDED;
      --bs-text-body:#C9C9CE;
      --bs-text-mid:#9A9AA0;
      --bs-text-muted:#8E8E93;
      --bs-text-label:#7A7A80;
      --bs-text-dim:#6B6B70;
      --bs-text-faint:#5C5C62;
      --bs-surface:#151515;
      --bs-card-a:#161616;
      --bs-card-b:#101010;
      --bs-card-hover:#141417;
      --bs-menu:#16161A;
      --bs-thumb:#16161B;
      --bs-input:#0C0C0E;
      --bs-well:#0E0E11;
      --bs-disabled:#1C1C20;
      --bs-chip:#1A1A1E;
      --bs-float-chip:rgba(10,10,12,0.6);
      --bs-skel-a:#1A1A1A;
      --bs-skel-b:#1E1E1E;
      --bs-line:rgba(255,255,255,0.06);
      --bs-border:rgba(255,255,255,0.08);
      --bs-border-2:rgba(255,255,255,0.11);
      --bs-border-3:rgba(255,255,255,0.18);
      --bs-row-hover:rgba(255,255,255,0.025);
      --bs-hover:rgba(255,255,255,0.05);
      --bs-wave-off:rgba(255,255,255,0.12);
      --bs-seg-on-bg:rgba(255,255,255,0.1);
      --bs-seg-on-text:#fff;
      --bs-btn-bg:#fff;
      --bs-btn-text:#0A0A0A;
      --bs-blue-soft:#6FA0FF;
      --bs-green:#2FD080;
      --bs-red:#FF6B60;
      --bs-amber:#FFB400;
      --bs-red-bg:rgba(255,69,58,0.1);
      --bs-red-border:rgba(255,69,58,0.3);
      --bs-amber-bg:rgba(255,180,0,0.15);
      --bs-overlay:rgba(4,4,6,0.74);
      --bs-shadow:rgba(0,0,0,0.5);
      --bs-shadow-strong:rgba(0,0,0,0.6);
    }
    .beatshop *{box-sizing:border-box}
    .beatshop ::selection{background:rgba(47,107,255,0.3)}
    .beatshop input::placeholder{color:var(--bs-text-faint)}
    .beatshop button,.beatshop input,.beatshop select,.beatshop textarea{font-family:inherit}
    @keyframes bs-eq{0%,100%{transform:scaleY(0.35)}50%{transform:scaleY(1)}}
    @keyframes bs-sh{0%{background-position:-260px 0}100%{background-position:260px 0}}
    @keyframes bs-pop{0%{transform:scale(0.7);opacity:0}60%{transform:scale(1.08)}100%{transform:scale(1);opacity:1}}
    @keyframes bs-rise{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
    @keyframes bs-spin{to{transform:rotate(360deg)}}
    .beatshop .bs-row:hover{background:var(--bs-row-hover)!important}
    .beatshop .bs-card:hover{background:var(--bs-card-hover)!important;border-color:rgba(47,107,255,0.4)!important;transform:translateY(-3px)!important}
    .beatshop .bs-heart:hover{background:var(--bs-hover)!important}
    .beatshop .bs-vrow:hover{background:var(--bs-hover)!important}
    .beatshop .bs-fmt:hover{background:var(--bs-hover)!important}
    .beatshop .bs-theme-toggle:hover{background:var(--bs-hover)!important}
    .beatshop .bs-tip{position:relative;display:inline-flex;align-items:center;vertical-align:-2px;margin-left:6px;cursor:help;outline:none}
    .beatshop .bs-tip-bubble{position:absolute;bottom:calc(100% + 9px);left:-10px;width:250px;background:var(--bs-menu);border:1px solid var(--bs-border-2);border-radius:11px;padding:10px 13px;box-shadow:0 12px 34px var(--bs-shadow-strong);font-size:12px;font-weight:500;line-height:1.55;letter-spacing:normal;text-transform:none;color:var(--bs-text-body);opacity:0;pointer-events:none;transition:opacity .12s ease;z-index:60}
    .beatshop .bs-tip:hover .bs-tip-bubble,.beatshop .bs-tip:focus .bs-tip-bubble{opacity:1}
    .beatshop .bs-tip:hover svg,.beatshop .bs-tip:focus svg{stroke:var(--bs-text-body)}
    .beatshop input.nova-range{-webkit-appearance:none;appearance:none;position:absolute;top:0;left:0;width:100%;height:26px;margin:0;background:transparent;pointer-events:none}
    .beatshop input.nova-range::-webkit-slider-runnable-track{background:transparent;height:26px}
    .beatshop input.nova-range::-moz-range-track{background:transparent;height:26px}
    .beatshop input.nova-range::-webkit-slider-thumb{-webkit-appearance:none;appearance:none;pointer-events:auto;width:18px;height:18px;margin-top:4px;border-radius:50%;background:#fff;border:2px solid #2F6BFF;box-shadow:0 2px 6px var(--bs-shadow);cursor:pointer}
    .beatshop input.nova-range::-moz-range-thumb{pointer-events:auto;width:18px;height:18px;border-radius:50%;background:#fff;border:2px solid #2F6BFF;box-shadow:0 2px 6px var(--bs-shadow);cursor:pointer}
    .beatshop .bs-waveform{width:100%;max-width:100%;min-width:0;overflow:hidden}
    /* Beat detail title — override body inherited line-height (120% → ~14px) which
       collapses wrapped lines on large headings. */
    .beatshop .bs-beat-title{
      display:block;
      width:100%;
      max-width:100%;
      margin:0;
      padding:0;
      font-weight:700;
      font-size:clamp(22px, 6.2vw, 32px);
      letter-spacing:-0.02em;
      line-height:1.3 !important;
      color:var(--bs-text);
      overflow-wrap:anywhere;
      word-break:break-word;
      hyphens:auto;
    }
    .beatshop .bs-beat-title-actions{
      display:flex;
      align-items:center;
      gap:10px;
      flex-wrap:wrap;
      margin-top:12px;
    }
    /* My Vault */
    .beatshop .bs-vault-heading{
      margin:0 0 26px;
      font-weight:700;
      font-size:clamp(28px, 7vw, 40px);
      letter-spacing:-0.025em;
      line-height:1.2 !important;
      color:var(--bs-text);
    }
    .beatshop .bs-vault-np{
      display:flex;
      align-items:center;
      gap:18px;
      margin-bottom:18px;
    }
    .beatshop .bs-vault-np-info{flex:1;min-width:0}
    .beatshop .bs-vault-np-title{
      font-weight:700;
      font-size:clamp(18px, 4.5vw, 24px);
      letter-spacing:-0.01em;
      line-height:1.3 !important;
      color:var(--bs-text);
      overflow-wrap:anywhere;
      word-break:break-word;
    }
    .beatshop .bs-vault-np-meta{
      font-size:13px;
      color:var(--bs-text-muted);
      margin-top:4px;
      line-height:1.4 !important;
    }
    .beatshop .bs-vault-np-actions{flex-shrink:0}
    .beatshop .bs-vault-controls{
      display:flex;
      align-items:center;
      justify-content:center;
      gap:20px;
      flex-wrap:wrap;
    }
    .beatshop .bs-vault-list-head,
    .beatshop .bs-vrow{
      display:grid;
      grid-template-columns:30px 1fr 96px 110px 116px 70px;
      gap:14px;
      align-items:center;
      padding:12px 14px;
    }
    .beatshop .bs-vault-list-head{
      padding:0 14px 12px;
      border-bottom:1px solid var(--bs-border);
      font-size:11px;
      font-weight:600;
      letter-spacing:0.08em;
      color:var(--bs-text-label);
    }
    .beatshop .bs-vrow{
      border-bottom:1px solid var(--bs-line);
      cursor:pointer;
      border-radius:10px;
      transition:background .12s;
      background:transparent;
    }
    .beatshop .bs-vrow-active{background:rgba(47,107,255,0.07)}
    .beatshop .bs-vrow-idx{display:flex;align-items:center;justify-content:center}
    .beatshop .bs-vrow-track{display:flex;align-items:center;gap:13px;min-width:0}
    .beatshop .bs-vrow-thumb{
      width:40px;height:40px;border-radius:9px;flex-shrink:0;
      background:radial-gradient(28px 20px at 65% 25%, rgba(47,107,255,0.25), transparent),var(--bs-thumb);
      border:1px solid var(--bs-border);
      display:flex;align-items:center;justify-content:center;
    }
    .beatshop .bs-vrow-name{
      font-weight:600;
      font-size:15px;
      line-height:1.3 !important;
      overflow-wrap:anywhere;
      word-break:break-word;
    }
    .beatshop .bs-vrow-sub{display:none}
    .beatshop .bs-vrow-genres-desktop{
      font-size:12px;color:var(--bs-text-label);margin-top:2px;
      white-space:nowrap;overflow:hidden;text-overflow:ellipsis;
    }
    .beatshop .bs-vrow-cell{
      font-size:13px;color:var(--bs-text-body);font-variant-numeric:tabular-nums;
    }
    .beatshop .bs-vrow-dl{display:flex;justify-content:flex-end;color:var(--bs-text-faint)}
    .beatshop .bs-seller-row-mobile-meta{display:none}
    .beatshop .bs-admin-cat-mobile-meta{display:none}
    .beatshop .bs-admin-orders-mobile-meta{display:none}
    .beatshop .bs-admin-audit-mobile-meta{display:none}
    /* Browse page list */
    .beatshop .bs-browse-list{max-width:100%}
    .beatshop .bs-browse-list-head{
      display:grid;gap:14px;padding:0 6px 12px;
      border-bottom:1px solid var(--bs-border);
      grid-template-columns:minmax(180px,1fr) 96px 110px 116px 96px;
      font-size:11px;font-weight:600;letter-spacing:0.08em;color:var(--bs-text-label);
    }
    .beatshop .bs-browse-list-row{
      display:grid;gap:14px;align-items:center;
      padding:13px 6px;
      border-bottom:1px solid var(--bs-line);
      cursor:pointer;transition:background .12s;
      grid-template-columns:minmax(180px,1fr) 96px 110px 116px 96px;
    }
    .beatshop .bs-browse-beat-name{font-weight:600;font-size:15px;color:var(--bs-text)}
    .beatshop .bs-browse-beat-sub{
      font-size:12px;color:var(--bs-text-label);margin-top:2px;
      white-space:nowrap;overflow:hidden;text-overflow:ellipsis;
    }
    .beatshop .bs-browse-beat-mobile-meta{display:none}
    .bs-miniplayer{
      position:fixed;z-index:10050;bottom:16px;
      left:50%;transform:translateX(-50%);
      width:min(560px, calc(100vw - 24px));
      box-sizing:border-box;
      display:flex;align-items:center;gap:12px;padding:12px 14px;
      background:var(--bs-menu);border:1px solid var(--bs-border-2);border-radius:14px;
      box-shadow:0 12px 32px var(--bs-shadow-strong);backdrop-filter:blur(10px);
    }
    .bs-miniplayer-controls{display:flex;align-items:center;gap:4px;flex-shrink:0}
    .bs-miniplayer-thumb{flex-shrink:0;display:flex}
    .bs-miniplayer-info{flex:1;min-width:0}
    .bs-miniplayer-title{
      font-weight:600;font-size:14.5px;color:var(--bs-text-hi);
      white-space:nowrap;overflow:hidden;text-overflow:ellipsis;
    }
    .bs-miniplayer-meta{
      font-size:12px;color:var(--bs-text-muted);margin-top:2px;
      font-variant-numeric:tabular-nums;
      white-space:nowrap;overflow:hidden;text-overflow:ellipsis;
    }
    @media (max-width:720px){
      .beatshop .bs-page-main{padding-left:16px!important;padding-right:16px!important}
      .beatshop .bs-stats{grid-template-columns:repeat(2,minmax(0,1fr))!important}
      /* Browse list */
      .beatshop .bs-browse-list-head,
      .beatshop .bs-browse-list-row{
        grid-template-columns:1fr auto!important;
        min-width:0!important;
        gap:10px!important;
      }
      .beatshop .bs-browse-col-desktop{display:none!important}
      .beatshop .bs-browse-beat-mobile-meta{
        display:block!important;
        font-size:12px;
        color:var(--bs-text-label);
        margin-top:3px;
        line-height:1.4!important;
      }
      .beatshop .bs-browse-beat-name{
        font-size:14px!important;
        line-height:1.3!important;
        overflow-wrap:anywhere;
        word-break:break-word;
      }
      /* Grid cards: 2-col on mobile */
      .beatshop .bs-browse-grid{grid-template-columns:repeat(2,minmax(0,1fr))!important;gap:10px!important}
      /* Dropdown panels: keep in viewport on mobile */
      .beatshop .bs-dropdown-panel{
        right:auto!important;
        left:0!important;
        min-width:0!important;
        max-width:calc(100vw - 48px)!important;
        max-height:55vh!important;
        overflow:auto!important;
        z-index:10060!important;
      }
      .beatshop .bs-dropdown-wrap:has(.bs-tempo-panel){position:static!important}
      .beatshop .bs-sort-wrap{position:static!important}
      .beatshop .bs-sort-wrap .bs-dropdown-panel{
        right:0!important;
        left:auto!important;
      }
      .beatshop .bs-tempo-panel{
        position:absolute!important;
        left:0!important;
        right:0!important;
        top:100%!important;
        width:auto!important;
        max-width:none!important;
        margin-top:6px;
      }
      /* Seller Studio mobile */
      .beatshop .bs-seller-tabs{
        width:100%!important;
        overflow-x:auto;
        -webkit-overflow-scrolling:touch;
      }
      .beatshop .bs-seller-tabs button{
        font-size:12px!important;
        padding:8px 10px!important;
        white-space:nowrap;
      }
      .beatshop .bs-seller-kpis{
        grid-template-columns:1fr!important;
        gap:10px!important;
      }
      .beatshop .bs-seller-sales-head,
      .beatshop .bs-seller-sales-row{
        grid-template-columns:1fr auto auto!important;
        gap:10px!important;
        padding-left:6px!important;
        padding-right:6px!important;
      }
      .beatshop .bs-seller-col-desktop{display:none!important}
      .beatshop .bs-seller-row-mobile-meta{
        display:block!important;
        font-size:11.5px;
        color:var(--bs-text-label);
        margin-top:3px;
      }
      .beatshop .bs-seller-form{
        margin-left:0!important;
        margin-right:0!important;
        max-width:100%!important;
      }
      .beatshop .bs-admin-catalog-filter{width:100%!important;max-width:260px}
      .beatshop .bs-admin-catalog-head,
      .beatshop .bs-admin-catalog-row{
        grid-template-columns:1fr auto auto!important;
        gap:9px!important;
        padding-left:6px!important;
        padding-right:6px!important;
      }
      .beatshop .bs-admin-cat-desktop{display:none!important}
      .beatshop .bs-admin-catalog-head > :nth-child(6),
      .beatshop .bs-admin-catalog-head > :nth-child(7){text-align:right}
      .beatshop .bs-admin-cat-mobile-meta{
        display:block!important;
        font-size:11px;
        color:var(--bs-text-label);
        margin-top:2px;
        line-height:1.35!important;
      }
      .beatshop .bs-admin-cat-status{
        justify-self:end;
        font-size:11px!important;
      }
      .beatshop .bs-admin-cat-favorites{
        justify-self:end;
      }
      .beatshop .bs-admin-cat-preview{
        display:flex;
        flex-direction:column;
        align-items:flex-end;
      }
      .beatshop .bs-admin-cat-actions{
        display:flex!important;
        flex-direction:column;
        align-items:flex-end;
        gap:6px!important;
      }
      .beatshop .bs-admin-orders-head,
      .beatshop .bs-admin-orders-row{
        grid-template-columns:1fr auto auto auto!important;
        gap:9px!important;
        padding-left:6px!important;
        padding-right:6px!important;
      }
      .beatshop .bs-admin-orders-desktop{display:none!important}
      .beatshop .bs-admin-orders-mobile-meta{
        display:block!important;
        font-size:11px;
        color:var(--bs-text-label);
        margin-top:2px;
        line-height:1.35!important;
      }
      .beatshop .bs-admin-orders-status,
      .beatshop .bs-admin-orders-gross,
      .beatshop .bs-admin-orders-action{justify-self:end}
      .beatshop .bs-admin-fin-kpis{
        grid-template-columns:1fr 1fr!important;
        gap:10px!important;
      }
      .beatshop .bs-admin-fin-kpis > div{
        padding:14px 14px!important;
      }
      .beatshop .bs-admin-fin-kpis > div > div:last-child{
        font-size:16px!important;
        line-height:1.3!important;
        overflow-wrap:anywhere;
        word-break:break-word;
      }
      .beatshop .bs-admin-fin-row{
        display:flex!important;
        flex-direction:column!important;
        align-items:flex-start!important;
        gap:3px;
      }
      .beatshop .bs-admin-fin-row > span:last-child{
        font-size:12px;
        color:var(--bs-text-label);
      }
      .beatshop .bs-admin-audit-head,
      .beatshop .bs-admin-audit-row{
        grid-template-columns:1fr auto!important;
        gap:9px!important;
        padding-left:6px!important;
        padding-right:6px!important;
      }
      .beatshop .bs-admin-audit-desktop{display:none!important}
      .beatshop .bs-admin-audit-mobile-meta{
        display:block!important;
        grid-column:1 / -1;
        font-size:11px;
        color:var(--bs-text-label);
        line-height:1.35!important;
      }
      /* Browse page heading */
      .beatshop h1{font-size:clamp(28px,7vw,40px)!important;line-height:1.2!important}
      .beatshop .bs-beat-title{font-size:22px!important;line-height:1.35!important;letter-spacing:-0.01em!important}
      .beatshop .bs-beat-title-actions{margin-top:14px}
      .beatshop .bs-beat-meta{font-size:12.5px!important;line-height:1.5!important}
      .beatshop .bs-vault-heading{margin-bottom:18px}
      .beatshop .bs-vault-np{
        display:grid;
        grid-template-columns:60px 1fr;
        grid-template-areas:
          "art info"
          "actions actions";
        gap:12px 14px;
        align-items:start;
      }
      .beatshop .bs-vault-np-art{grid-area:art}
      .beatshop .bs-vault-np-info{grid-area:info}
      .beatshop .bs-vault-np-actions{grid-area:actions}
      .beatshop .bs-vault-np-title{font-size:18px!important;line-height:1.35!important}
      .beatshop .bs-vault-controls{gap:12px}
      .beatshop .bs-vault-list-head,
      .beatshop .bs-vrow{
        grid-template-columns:28px 1fr 44px;
        gap:10px;
        padding-left:8px;
        padding-right:8px;
      }
      .beatshop .bs-vault-col-desktop{display:none!important}
      .beatshop .bs-vrow-sub{
        display:block;
        font-size:11.5px;
        color:var(--bs-text-label);
        margin-top:3px;
        line-height:1.4 !important;
        white-space:nowrap;
        overflow:hidden;
        text-overflow:ellipsis;
      }
      .beatshop .bs-vrow-genres-desktop{display:none}
      .bs-miniplayer{
        bottom:12px;
        width:calc(100vw - 16px);
        max-width:none;
        display:grid;
        grid-template-columns:auto 1fr auto auto;
        grid-template-areas:
          "thumb info fav close"
          "controls controls controls controls";
        gap:10px 8px;
        padding:12px 12px 10px;
        align-items:center;
      }
      .bs-miniplayer-controls{
        grid-area:controls;
        justify-content:center;
        gap:10px;
        padding-top:2px;
        border-top:1px solid var(--bs-line);
      }
      .bs-miniplayer-thumb{grid-area:thumb}
      .bs-miniplayer-info{grid-area:info;min-width:0}
      .bs-miniplayer-fav{grid-area:fav}
      .bs-miniplayer-close{grid-area:close}
      .bs-miniplayer-title{font-size:13.5px}
      .bs-miniplayer-meta{font-size:11.5px}
      .bs-miniplayer-play{width:40px!important;height:40px!important}
    }
  `}</style>
);

const SunIcon: React.FC = () => (
  <svg width="15" height="15" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
    <circle cx="9" cy="9" r="3.6" />
    <path d="M9 1.5v2M9 14.5v2M1.5 9h2M14.5 9h2M3.7 3.7l1.4 1.4M12.9 12.9l1.4 1.4M14.3 3.7l-1.4 1.4M5.1 12.9l-1.4 1.4" />
  </svg>
);

const MoonIcon: React.FC = () => (
  <svg width="15" height="15" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M15.5 10.7A6.6 6.6 0 0 1 7.3 2.5a6.6 6.6 0 1 0 8.2 8.2z" />
  </svg>
);

export const PageShell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setTheme] = React.useState<BsTheme>(getStoredTheme);
  const toggle = () => setTheme((t) => {
    const next: BsTheme = t === 'light' ? 'dark' : 'light';
    localStorage.setItem(THEME_KEY, next);
    return next;
  });
  return (
    <div className={theme === 'dark' ? 'beatshop bs-dark' : 'beatshop'}
      style={s("min-height:100%;background:var(--bs-bg);color:var(--bs-text);font-family:'Inter',system-ui,sans-serif;-webkit-font-smoothing:antialiased;transition:background .15s ease,color .15s ease")}>
      <GlobalStyles />
      <BsThemeContext.Provider value={theme}>
      <main className="bs-page-main" style={s('position:relative;padding:22px 32px 120px;max-width:1180px;margin:0 auto')}>
        <button
          className="bs-theme-toggle"
          onClick={toggle}
          aria-label={theme === 'light' ? 'Switch to dark theme' : 'Switch to light theme'}
          title={theme === 'light' ? 'Switch to dark theme' : 'Switch to light theme'}
          style={s('position:absolute;top:20px;right:32px;z-index:40;display:flex;align-items:center;justify-content:center;width:32px;height:32px;border-radius:50%;background:var(--bs-surface);border:1px solid var(--bs-border-2);color:var(--bs-text-body);cursor:pointer;transition:all .12s')}
        >
          {theme === 'light' ? <MoonIcon /> : <SunIcon />}
        </button>
        {children}
      </main>
      <MiniPlayerBar />
      </BsThemeContext.Provider>
    </div>
  );
};

/** Small (i) icon that reveals an explanatory tooltip on hover or keyboard focus. */
export const InfoTip: React.FC<{ text: React.ReactNode }> = ({ text }) => (
  <span className="bs-tip" tabIndex={0} role="note" aria-label={typeof text === 'string' ? text : undefined}>
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="var(--bs-text-faint)" strokeWidth="1.4" style={{ transition: 'stroke .12s' }}>
      <circle cx="8" cy="8" r="6.6" />
      <path d="M8 7.4v3.8" strokeLinecap="round" />
      <circle cx="8" cy="4.9" r="0.9" fill="var(--bs-text-faint)" stroke="none" />
    </svg>
    <span className="bs-tip-bubble">{text}</span>
  </span>
);

export const StatCard: React.FC<{ label: string; value: React.ReactNode; sub: React.ReactNode; subColor?: string; icon: React.ReactNode }> =
  ({ label, value, sub, subColor = 'var(--bs-text-dim)', icon }) => (
    <div style={s('background:linear-gradient(180deg,var(--bs-card-a),var(--bs-card-b));border:1px solid var(--bs-border);border-radius:16px;padding:18px 19px')}>
      <div style={s('display:flex;align-items:center;justify-content:space-between;margin-bottom:18px')}>
        <span style={s('font-size:10.5px;font-weight:600;letter-spacing:0.13em;color:var(--bs-text-label)')}>{label}</span>
        <svg width="16" height="16" viewBox="0 0 18 18" fill="none" stroke="var(--bs-text-faint)" strokeWidth="1.5">{icon}</svg>
      </div>
      <div style={s('font-weight:700;font-size:28px;letter-spacing:-0.02em')}>{value}</div>
      <div style={{ ...s('font-size:12px;margin-top:5px'), color: subColor }}>{sub}</div>
    </div>
  );

export const SpecRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div style={s('display:flex;align-items:center;justify-content:space-between;padding:13px 0;border-bottom:1px solid var(--bs-line)')}>
    <span style={s('color:var(--bs-text-muted);font-size:13px')}>{label}</span>
    <span style={s('font-weight:500;font-size:13.5px;color:var(--bs-text-hi)')}>{value}</span>
  </div>
);

export const HeartIcon: React.FC<{ filled: boolean; size?: number; stroke?: string }> = ({ filled, size = 17, stroke }) => (
  <svg width={size} height={size} viewBox="0 0 18 18" fill={filled ? BLUE : 'none'} stroke={filled ? BLUE : (stroke || 'var(--bs-text-dim)')} strokeWidth="1.6">
    <path d="M9 15.5S2.5 11 2.5 6.6A3.6 3.6 0 0 1 9 4.4 3.6 3.6 0 0 1 15.5 6.6C15.5 11 9 15.5 9 15.5z" />
  </svg>
);

export const DiscIcon: React.FC<{ size?: number }> = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 20 20" fill="none" stroke="var(--bs-blue-soft)" strokeWidth="1.4">
    <circle cx="10" cy="10" r="6.5" /><circle cx="10" cy="10" r="1.4" />
  </svg>
);

export const BeatThumb: React.FC<{ size?: number }> = ({ size = 44 }) => (
  <div style={{ ...s('border-radius:10px;background:radial-gradient(30px 22px at 65% 25%, rgba(47,107,255,0.25), transparent),var(--bs-thumb);border:1px solid var(--bs-border);display:flex;align-items:center;justify-content:center;flex-shrink:0'), width: size, height: size }}>
    <DiscIcon size={Math.round(size * 0.45)} />
  </div>
);

export const NewBadge: React.FC = () => (
  <span style={s('padding:2px 7px;background:#2F6BFF;color:#fff;font-size:9px;font-weight:700;letter-spacing:0.06em;border-radius:5px')}>NEW</span>
);

export const Equalizer: React.FC<{ height?: number }> = ({ height = 24 }) => (
  <div style={{ ...s('display:flex;align-items:flex-end;gap:3px'), height }}>
    {[0, 0.2, 0.4].map((d, i) => (
      <div key={i} style={{ ...s('width:3px;height:100%;background:#2F6BFF;border-radius:2px;transform-origin:bottom'), animation: `bs-eq .6s ease-in-out infinite ${d}s` }} />
    ))}
  </div>
);

export const Dropdown: React.FC<{
  label: React.ReactNode;
  open: boolean;
  onToggle: () => void;
  minWidth: number;
  maxHeight?: number;
  children: React.ReactNode;
}> = ({ label, open, onToggle, minWidth, maxHeight, children }) => (
  <div className="bs-dropdown-wrap" style={s('position:relative')}>
    <button onClick={onToggle} style={s('display:flex;align-items:center;gap:7px;padding:8px 13px;background:var(--bs-surface);border:1px solid var(--bs-border);border-radius:9px;font-weight:500;font-size:13px;color:var(--bs-text-body);cursor:pointer')}>
      {label} <span style={s('color:var(--bs-text-faint);font-size:9px')}>▾</span>
    </button>
    {open && (
      <div className="bs-dropdown-panel" style={{ ...s('position:absolute;right:0;top:38px;z-index:30;background:var(--bs-menu);border:1px solid var(--bs-border-2);border-radius:12px;padding:6px;box-shadow:0 16px 44px var(--bs-shadow-strong)'), minWidth, ...(maxHeight ? { maxHeight, overflow: 'auto' as const } : {}) }}>
        {children}
      </div>
    )}
  </div>
);

export const MenuItem: React.FC<{ active: boolean; onSelect: () => void; children: React.ReactNode }> = ({ active, onSelect, children }) => (
  <button onClick={onSelect} style={s(
    'display:block;width:100%;text-align:left;padding:9px 11px;border:none;border-radius:8px;font-size:13px;cursor:pointer;' +
    (active ? 'background:rgba(47,107,255,0.16);color:var(--bs-blue-soft);font-weight:600' : 'background:transparent;color:var(--bs-text-body)')
  )}>{children}</button>
);

export const PrimaryBtn: React.FC<{ onClick?: () => void; disabled?: boolean; style?: string; children: React.ReactNode }> =
  ({ onClick, disabled, style = '', children }) => (
    <button onClick={onClick} disabled={disabled} style={s(
      (disabled
        ? 'background:var(--bs-disabled);color:var(--bs-text-faint);cursor:not-allowed'
        : 'background:var(--bs-btn-bg);color:var(--bs-btn-text);cursor:pointer') +
      ';padding:12px 20px;border:none;border-radius:10px;font-weight:600;font-size:14px;' + style
    )}>{children}</button>
  );

export const GhostBtn: React.FC<{ onClick?: () => void; disabled?: boolean; style?: string; children: React.ReactNode }> = ({ onClick, disabled, style = '', children }) => (
  <button onClick={onClick} disabled={disabled} style={s('padding:11px 20px;background:transparent;border:1px solid var(--bs-border-3);border-radius:10px;font-weight:600;font-size:14px;' +
    (disabled ? 'color:var(--bs-text-faint);opacity:0.5;cursor:not-allowed;' : 'color:var(--bs-text-hi);cursor:pointer;') + style)}>{children}</button>
);

export const EmptyState: React.FC<{ icon?: React.ReactNode; title: string; body: string; action?: React.ReactNode }> =
  ({ icon, title, body, action }) => (
    <div style={s('display:flex;flex-direction:column;align-items:center;justify-content:center;padding:72px 20px;text-align:center;border:1px dashed var(--bs-border-2);border-radius:18px;background:var(--bs-well);margin-top:10px')}>
      {icon}
      <div style={s('font-weight:700;font-size:19px;color:var(--bs-text-hi)')}>{title}</div>
      <p style={s('margin:9px 0 20px;color:var(--bs-text-muted);font-size:13.5px;max-width:340px;line-height:1.5')}>{body}</p>
      {action}
    </div>
  );

export const SkeletonRows: React.FC = () => (
  <div style={s('display:flex;flex-direction:column;gap:2px')}>
    {[1, 2, 3, 4, 5, 6, 7].map((i) => (
      <div key={i} style={s('display:flex;align-items:center;gap:13px;padding:14px 6px;border-bottom:1px solid var(--bs-line)')}>
        <div style={s('width:44px;height:44px;border-radius:10px;background:linear-gradient(100deg,var(--bs-skel-a) 30%,var(--bs-skel-b) 50%,var(--bs-skel-a) 70%);background-size:200px 100%;animation:bs-sh 1.1s linear infinite')} />
        <div style={s('flex:1;display:flex;flex-direction:column;gap:8px')}>
          <div style={s('height:13px;width:140px;border-radius:5px;background:var(--bs-skel-a)')} />
          <div style={s('height:11px;width:90px;border-radius:5px;background:var(--bs-skel-b)')} />
        </div>
        <div style={s('height:13px;width:80px;border-radius:5px;background:var(--bs-skel-a)')} />
      </div>
    ))}
  </div>
);

/** Static waveform of seeded bars with progress fill. */
export const Waveform: React.FC<{
  seed: number; count?: number; height?: number; progress?: number; onScrub?: (frac: number) => void;
}> = ({ seed, count = 88, height = 92, progress = 0, onScrub }) => {
  const bars = React.useMemo(() => genBars(seed, count, 10, 100), [seed, count]);
  return (
    <div
      className="bs-waveform"
      onClick={onScrub ? (e) => {
        const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
        onScrub(Math.max(0, Math.min(1, (e.clientX - r.left) / r.width)));
      } : undefined}
      style={{ ...s('display:flex;align-items:center;gap:1px;width:100%;max-width:100%;min-width:0;overflow:hidden'), height, cursor: onScrub ? 'pointer' : 'default' }}
    >
      {bars.map((h, i) => (
        <div key={i} style={{
          ...s('flex:1 1 0;border-radius:2px;min-width:0;max-width:100%;transition:background .05s linear'),
          height: `${h}%`,
          background: i / count <= progress ? BLUE : 'var(--bs-wave-off)',
        }} />
      ))}
    </div>
  );
};

export const seg = 'padding:7px 14px;border-radius:7px;font-weight:600;font-size:13px;cursor:pointer;border:none;transition:all .12s;';
export const segOn = seg + 'background:var(--bs-seg-on-bg);color:var(--bs-seg-on-text)';
export const segOff = seg + 'background:transparent;color:var(--bs-text-muted)';
export const cardPanel = 'background:linear-gradient(180deg,var(--bs-card-a),var(--bs-card-b));border:1px solid var(--bs-border);border-radius:20px;';
export const inputStyle = 'width:100%;padding:11px 13px;background:var(--bs-input);border:1px solid var(--bs-border-2);border-radius:10px;color:var(--bs-text);font-size:13.5px;outline:none';
export const labelStyle = 'font-size:11px;font-weight:600;letter-spacing:0.1em;color:var(--bs-text-label);margin-bottom:7px;display:block';
